// pages/WaitlistTab.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getProviderWaitlistByDate } from "../APIs/waitlistApi";

// ---- helpers ----
const ZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Toronto",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Tokyo",
];

const defaultZone = (() => {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"
    );
  } catch {
    return "America/New_York";
  }
})();

function todayYYYYMMDD(zoneId = defaultZone) {
  // keep simple: use local browser date
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function fmtDate(zdtLike) {
  if (!zdtLike) return "—";
  const iso = String(zdtLike).split("[")[0];
  return new Date(iso).toLocaleDateString([], {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function fmtTime(zdtLike) {
  if (!zdtLike) return "";
  const iso = String(zdtLike).split("[")[0];
  return new Date(iso).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function WaitlistTab({ providerId: providerIdProp }) {
  // Get providerId from route or prop
  const { providerId: providerIdParam } = useParams();
  const providerId = providerIdProp ?? providerIdParam;

  const [date, setDate] = useState(() => todayYYYYMMDD());
  const [zone, setZone] = useState(defaultZone);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [items, setItems] = useState([]); // array of waitlist entries with patient info

  const fetchList = async () => {
    if (!providerId) return;
    setLoading(true);
    setErr("");
    try {
      const data = await getProviderWaitlistByDate(Number(providerId), {
        date,
        zone,
      });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e?.message || "Failed to load waitlisted patients");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, date, zone]);

  return (
    <div className="max-w-4xl p-4 space-y-4">
      <h3 className="text-xl font-semibold">Waitlisted Patients</h3>

      {/* Filters - Only date and timezone, no time picker needed */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block mb-1 font-medium">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full border rounded px-3 py-2"
          />
        </div>
        <div>
          <label className="block mb-1 font-medium">Time Zone</label>
          <select
            value={zone}
            onChange={(e) => setZone(e.target.value)}
            className="w-full border rounded px-3 py-2"
          >
            {ZONES.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <button
            onClick={fetchList}
            className="w-full px-4 py-2 rounded bg-gray-800 text-white hover:bg-black"
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="min-h-[120px]">
        {loading && <div className="text-gray-600">Loading…</div>}
        {err && !loading && <div className="text-red-600">{err}</div>}

        {!loading && !err && (
          <div className="grid gap-3">
            {items.length === 0 ? (
              <div className="text-gray-500">
                No waitlisted patients for this date.
              </div>
            ) : (
              items.map((row) => {
                const startDate = fmtDate(row.appointmentStart);
                const startTime = fmtTime(row.appointmentStart);
                const endDate = fmtDate(row.appointmentEnd);
                const endTime = fmtTime(row.appointmentEnd);
                const fullName = [row.patientFirstName, row.patientLastName]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <div
                    key={row.waitlistId}
                    className="w-full bg-white border rounded-2xl shadow-sm p-4"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="text-lg font-semibold">
                          {fullName || "—"}
                        </div>
                        <div className="text-sm text-gray-600">
                          {row.patientEmail || "—"}
                          {row.patientPhone ? ` • ${row.patientPhone}` : ""}
                        </div>

                        <div className="mt-2 text-sm text-gray-700">
                          <div>
                            <span className="font-medium">Start:</span>{" "}
                            {startDate} • {startTime}
                          </div>
                          {row.appointmentEnd && (
                            <div>
                              <span className="font-medium">End:</span>{" "}
                              {endDate} • {endTime}
                            </div>
                          )}
                        </div>

                        {row.reasonForWaitlist && (
                          <div className="text-sm text-gray-600 mt-2">
                            <span className="font-medium">Reason:</span>{" "}
                            {row.reasonForWaitlist}
                          </div>
                        )}
                      </div>

                      <span
                        className={`px-2 py-1 rounded text-xs font-semibold ${
                          row.state === "PENDING"
                            ? "bg-yellow-100 text-yellow-700"
                            : row.state === "NOTIFIED"
                              ? "bg-blue-100 text-blue-700"
                              : row.state === "ACCEPTED"
                                ? "bg-green-100 text-green-700"
                                : row.state === "DECLINED"
                                  ? "bg-gray-200 text-gray-700"
                                  : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {row.state || "—"}
                      </span>
                    </div>

                    {/* Technical IDs (optional) */}
                    <div className="mt-2 text-xs text-gray-500">
                      Appt ID: {row.appointmentId} • Waitlist ID:{" "}
                      {row.waitlistId} • Patient ID: {row.patientId}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
